import { PermissionGuard } from '@/components/PermissionGuard';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDisableMember, useEnableMember, useUpdateMemberName, useUpdateMemberRole } from '@/hooks/useOrganizationMemberOperations';
import { useDeleteOrganizationMember } from '@/hooks/useOrganizationMembersActions';
import { useMemberAllowedSites } from '@/hooks/useMemberAllowedSites';
import { useUpdateMemberAllowedSites } from '@/hooks/useMemberAllowedSitesActions';
import { roleMapping } from '@/lib/roleMapping';
import type { Organization } from '@octave/contracts';
import type { OrganizationMember } from '@octave/contracts';
import {
  Building2,
  Loader2,
  Trash2,
  UserCheck,
  UserPen,
  UserX,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { OrganizationRole } from '@octave/contracts';

interface MembersSectionProps {
  organization: Organization | undefined;
  members: OrganizationMember[] | undefined;
  isMembersLoading: boolean;
}

export function MembersSection({
  organization,
  members,
  isMembersLoading,
}: MembersSectionProps) {
  const { mutate: deleteOrganizationMember, isPending: isDeletingMember } =
    useDeleteOrganizationMember();
  const { mutate: updateMemberName, isPending: isUpdatingUserName } =
    useUpdateMemberName();
  const { mutate: disableMember, isPending: isDisablingMember } =
    useDisableMember();
  const { mutate: enableMember, isPending: isEnablingMember } =
    useEnableMember();
  const { mutate: updateMemberRole, isPending: isUpdatingRole } =
    useUpdateMemberRole();

  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editRole, setEditRole] = useState<OrganizationRole>('VIEWER');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] =
    useState<OrganizationMember | null>(null);
  const [isToggleStatusDialogOpen, setIsToggleStatusDialogOpen] =
    useState(false);
  const [memberToToggle, setMemberToToggle] =
    useState<OrganizationMember | null>(null);
  const [isAllowedSitesDialogOpen, setIsAllowedSitesDialogOpen] =
    useState(false);
  const [allowedSitesMember, setAllowedSitesMember] =
    useState<OrganizationMember | null>(null);
  const [selectedAllowedSiteIds, setSelectedAllowedSiteIds] = useState<
    string[]
  >([]);
  const [isAllSites, setIsAllSites] = useState(true);

  const { data: memberAllowedSites, isLoading: isLoadingAllowedSites } =
    useMemberAllowedSites({
      organizationId: organization?.id,
      userId: allowedSitesMember?.userId,
    });
  const {
    mutate: updateMemberAllowedSites,
    isPending: isUpdatingAllowedSites,
  } = useUpdateMemberAllowedSites();

  const editingMember = useMemo(
    () =>
      members?.find((m: OrganizationMember) => m.userId === editingMemberId),
    [editingMemberId, members],
  );

  const adminCount = useMemo(
    () => members?.filter((m) => m.role === 'ADMIN' && !m.isDisabled).length ?? 0,
    [members],
  );

  const handleEditUserClick = (member: OrganizationMember) => {
    setEditingMemberId(member.userId);
    setEditFirstName(member.firstName ?? '');
    setEditLastName(member.lastName ?? '');
    setEditRole(member.role);
    setIsEditUserDialogOpen(true);
  };

  const handleSaveUser = () => {
    if (!editingMemberId || !organization?.id) return;
    const firstName = editFirstName.trim();
    const lastName = editLastName.trim();
    if (!firstName || !lastName) return;

    const roleChanged = editingMember && editRole !== editingMember.role;

    const closeDialog = () => {
      setIsEditUserDialogOpen(false);
      setEditingMemberId(null);
    };

    // Update name
    updateMemberName(
      { organizationId: organization.id, userId: editingMemberId, firstName, lastName },
      {
        onSuccess: () => {
          // Update role if changed
          if (roleChanged) {
            updateMemberRole(
              { organizationId: organization.id, userId: editingMemberId, role: editRole },
              { onSettled: closeDialog },
            );
          } else {
            closeDialog();
          }
        },
      },
    );
  };

  const confirmToggleMemberStatus = () => {
    if (!organization?.id || !memberToToggle) return;
    const mutate = memberToToggle.isDisabled ? enableMember : disableMember;
    mutate(
      { organizationId: organization.id, userId: memberToToggle.userId },
      { onSettled: () => { setIsToggleStatusDialogOpen(false); setMemberToToggle(null); } },
    );
  };

  useEffect(() => {
    if (memberAllowedSites !== undefined && allowedSitesMember) {
      if (memberAllowedSites.length === 0) {
        setIsAllSites(true);
        setSelectedAllowedSiteIds([]);
      } else {
        setIsAllSites(false);
        setSelectedAllowedSiteIds(memberAllowedSites);
      }
    }
  }, [memberAllowedSites, allowedSitesMember]);

  const handleSaveAllowedSites = () => {
    if (!organization?.id || !allowedSitesMember) return;
    updateMemberAllowedSites(
      { organizationId: organization.id, userId: allowedSitesMember.userId, siteIds: isAllSites ? [] : selectedAllowedSiteIds },
      { onSuccess: () => { setIsAllowedSitesDialogOpen(false); setAllowedSitesMember(null); } },
    );
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-slate-900">Membres</h2>
      </div>

      {isMembersLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : members && members.length === 0 ? (
        <p className="text-sm text-slate-500 py-4">
          Aucun membre dans l'organisation
        </p>
      ) : (
        <div className="space-y-2">
          {members?.map((member: OrganizationMember) => (
            <div
              key={`${member.userId}:${member.organizationId}`}
              className="bg-white rounded-xl px-5 py-4 hover:shadow-md transition-all flex items-center gap-4"
            >
              {/* Avatar */}
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm font-semibold">
                  {member.firstName && member.lastName
                    ? `${member.firstName[0]}${member.lastName[0]}`
                    : '??'}
                </AvatarFallback>
              </Avatar>

              {/* Name + Email */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {member.firstName && member.lastName
                    ? `${member.firstName} ${member.lastName}`
                    : '-'}
                </p>
                <p className="text-xs text-slate-500 truncate">{member.email}</p>
              </div>

              {/* Role + Sites badges */}
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                  {roleMapping[member.role]}
                </Badge>
                {member.allowedSitesCount > 0 && (
                  <Badge variant="outline" className="text-xs bg-violet-50 text-violet-600 border-violet-200">
                    {member.allowedSitesCount}/{organization?.totalSitesCount ?? organization?.sites?.length ?? '?'} C.F.
                  </Badge>
                )}
              </div>

              {/* Status dot */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                      member.isDisabled ? 'bg-red-500' : 'bg-emerald-500'
                    }`}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  {member.isDisabled ? 'Désactivé' : 'Actif'}
                </TooltipContent>
              </Tooltip>

              {/* Actions */}
              <PermissionGuard permission="manage">
                <div className="flex items-center gap-0.5 shrink-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700" onClick={() => { setAllowedSitesMember(member); setIsAllowedSitesDialogOpen(true); }}>
                        <Building2 className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Centres de facturation</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700" onClick={() => handleEditUserClick(member)}>
                        <UserPen className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Modifier</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${member.isDisabled ? 'text-green-600 hover:text-green-700' : 'text-orange-500 hover:text-orange-600'}`}
                        onClick={() => { setMemberToToggle(member); setIsToggleStatusDialogOpen(true); }}
                        disabled={isDisablingMember || isEnablingMember}
                      >
                        {member.isDisabled ? <UserCheck className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{member.isDisabled ? 'Activer' : 'Désactiver'}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => { setMemberToDelete(member); setIsDeleteDialogOpen(true); }}
                        disabled={isDeletingMember}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Supprimer</TooltipContent>
                  </Tooltip>
                </div>
              </PermissionGuard>
            </div>
          ))}
        </div>
      )}

      {/* Edit user dialog */}
      <Dialog open={isEditUserDialogOpen && !!editingMember} onOpenChange={(open) => { if (!open) { setIsEditUserDialogOpen(false); setEditingMemberId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le membre</DialogTitle>
            <DialogDescription>Mettez à jour le prénom et le nom.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Prénom</label>
              <input className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Nom</label>
              <input className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" value={editLastName} onChange={(e) => setEditLastName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Rôle</label>
              <div className="flex items-center gap-2">
                {(['ADMIN', 'EDITOR', 'VIEWER'] as const).map((role) => {
                  const isSelected = editRole === role;
                  const isLastAdmin = role !== 'ADMIN' && editingMember?.role === 'ADMIN' && adminCount <= 1;
                  return (
                    <button
                      key={role}
                      onClick={() => !isLastAdmin && setEditRole(role)}
                      disabled={isLastAdmin}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${
                        isSelected
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : isLastAdmin
                            ? 'bg-slate-50 text-slate-300 border-transparent cursor-not-allowed'
                            : 'bg-slate-50 text-slate-600 border-transparent hover:bg-slate-100'
                      }`}
                    >
                      {roleMapping[role] || role}
                    </button>
                  );
                })}
              </div>
              {editingMember?.role === 'ADMIN' && adminCount <= 1 && (
                <p className="text-xs text-amber-600">
                  Dernier administrateur — le rôle ne peut pas être modifié.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditUserDialogOpen(false); setEditingMemberId(null); }}>Annuler</Button>
            <Button onClick={handleSaveUser} disabled={isUpdatingUserName || isUpdatingRole || !editFirstName.trim() || !editLastName.trim()}>
              {(isUpdatingUserName || isUpdatingRole) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={isDeleteDialogOpen && !!memberToDelete} onOpenChange={(open) => { if (!open) { setIsDeleteDialogOpen(false); setMemberToDelete(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer le membre</DialogTitle>
            <DialogDescription>
              Supprimer <span className="font-semibold">{memberToDelete?.firstName} {memberToDelete?.lastName}</span> de l&apos;organisation ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsDeleteDialogOpen(false); setMemberToDelete(null); }} disabled={isDeletingMember}>Annuler</Button>
            <Button variant="destructive" onClick={() => {
              if (!organization?.id || !memberToDelete) return;
              deleteOrganizationMember({ organizationId: organization.id, userId: memberToDelete.userId }, { onSettled: () => { setIsDeleteDialogOpen(false); setMemberToDelete(null); } });
            }} disabled={isDeletingMember}>
              {isDeletingMember && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle status dialog */}
      <Dialog open={isToggleStatusDialogOpen && !!memberToToggle} onOpenChange={(open) => { if (!open) { setIsToggleStatusDialogOpen(false); setMemberToToggle(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={memberToToggle?.isDisabled ? 'text-green-600' : 'text-orange-600'}>
              {memberToToggle?.isDisabled ? 'Activer le membre' : 'Désactiver le membre'}
            </DialogTitle>
            <DialogDescription>
              {memberToToggle?.isDisabled ? 'Activer' : 'Désactiver'}{' '}
              <span className="font-semibold">{memberToToggle?.firstName} {memberToToggle?.lastName}</span> ?
              {memberToToggle?.isDisabled
                ? " Le membre pourra à nouveau accéder à l'organisation."
                : " Le membre ne pourra plus accéder à l'organisation."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsToggleStatusDialogOpen(false); setMemberToToggle(null); }} disabled={isDisablingMember || isEnablingMember}>Annuler</Button>
            <Button
              variant={memberToToggle?.isDisabled ? 'default' : 'destructive'}
              onClick={confirmToggleMemberStatus}
              disabled={isDisablingMember || isEnablingMember}
              className={memberToToggle?.isDisabled ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {(isDisablingMember || isEnablingMember) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {memberToToggle?.isDisabled ? 'Activer' : 'Désactiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allowed sites dialog */}
      <Dialog open={isAllowedSitesDialogOpen && !!allowedSitesMember} onOpenChange={(open) => { if (!open) { setIsAllowedSitesDialogOpen(false); setAllowedSitesMember(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Centres de facturation</DialogTitle>
            <DialogDescription>
              Centres accessibles pour <span className="font-semibold">{allowedSitesMember?.firstName} {allowedSitesMember?.lastName}</span>
            </DialogDescription>
          </DialogHeader>
          {isLoadingAllowedSites ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-3">
              <label className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/30 cursor-pointer">
                <input type="checkbox" checked={isAllSites} onChange={(e) => { setIsAllSites(e.target.checked); if (e.target.checked) setSelectedAllowedSiteIds([]); }} className="rounded" />
                <span className="text-sm font-medium">Tous les centres (aucune restriction)</span>
              </label>
              {!isAllSites && (
                <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                  {organization?.sites?.map((site) => (
                    <label key={site.id} className="flex items-center space-x-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                      <input type="checkbox" checked={selectedAllowedSiteIds.includes(site.id)} onChange={() => setSelectedAllowedSiteIds((prev) => prev.includes(site.id) ? prev.filter((id) => id !== site.id) : [...prev, site.id])} className="rounded" />
                      <span className="text-sm">{site.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAllowedSitesDialogOpen(false); setAllowedSitesMember(null); }} disabled={isUpdatingAllowedSites}>Annuler</Button>
            <Button onClick={handleSaveAllowedSites} disabled={isUpdatingAllowedSites || (!isAllSites && selectedAllowedSiteIds.length === 0)}>
              {isUpdatingAllowedSites && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
